import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Building2, FileText, KeyRound, CheckCircle, Loader2 } from "lucide-react";
import { HttpStatusCode } from "axios";
import logo from "assets/logo.svg";

import usePageLoading from "hooks/usePageLoading";

import CapturingInputs, { CapturingInputsRef } from "js/components/ExpressReport/CapturingInputs";
import Invalidated from "js/components/ExpressReport/Invalidated";
import PinConfirm, { PinConfirmRef } from "js/components/ExpressReport/PinConfirm";
import ThankYou from "js/components/ExpressReport/ThankYou";
import Welcome from "js/components/ExpressReport/Welcome";
import {
  CreditReport,
  Investigation,
  StepItem,
} from "js/components/ExpressReport/types";
import {
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonDivider,
  SkeletonHeading,
  SkeletonText,
  SlowStateBanner,
  StepsSkeleton,
  SkeletonBadge,
} from "js/components/Loading";
import { StepIndicator } from "js/components/ui/step-indicator";
import { Button } from "js/components/ui/button";

import { useGetInvestigation } from "../../../hooks/useInvestigations";
import { toast } from "../../../utils/toast";

const ExpressReport = () => {
  const { investigationId } = useParams();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isInvalidated, setIsInvalidated] = useState(false);
  const [isPinSubmitting, setIsPinSubmitting] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const capturingInputsRef = useRef<CapturingInputsRef>(null);
  const pinConfirmRef = useRef<PinConfirmRef>(null);
  const navigate = useNavigate();

  // Define steps for the step indicator
  const steps = [
    {
      number: 1,
      title: "Bienvenida",
      shortTitle: "Inicio",
      subtitle: "Introducción",
      icon: Building2,
      completed: currentStep > 1,
    },
    {
      number: 2,
      title: "Datos",
      shortTitle: "Datos",
      subtitle: "Información personal",
      icon: FileText,
      completed: currentStep > 2,
    },
    {
      number: 3,
      title: "Confirmar NIP",
      shortTitle: "NIP",
      subtitle: "Verificación",
      icon: KeyRound,
      completed: currentStep > 3,
    },
    {
      number: 4,
      title: "Completado",
      shortTitle: "Fin",
      subtitle: "Resultado",
      icon: CheckCircle,
      completed: currentStep > 4,
    },
  ];

  const {
    data: fetchedInvestigation,
    isLoading,
    error,
    refetch,
  } = useGetInvestigation(investigationId || "");
  const [investigation, setInvestigation] = useState<Investigation>();
  const [creditReport, setCreditReport] = useState<CreditReport>();
  const dataReady = Boolean(investigation && creditReport);

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading: isLoading || (!dataReady && !error),
  });

  const handleRetry = () => {
    dismissSlowState();
    void refetch();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <Button
          size="sm"
          variant="outline"
          className="border-amber-300 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetry}
        >
          Reintentar
        </Button>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const contentSkeleton = (
    <div className="space-y-6">
      <div className="space-y-6 rounded-2xl border border-slate-200/70 bg-white/95 p-6">
        <StepsSkeleton steps={4} />
        <SkeletonDivider />
        <SkeletonBlock className="mx-auto h-20 w-44 rounded" />
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <SkeletonBlock className="h-64 rounded-xl" />
          <div className="space-y-4">
            <SkeletonHeading className="h-8 w-56" />
            <div className="space-y-2">
              <SkeletonText className="w-full" />
              <SkeletonText className="w-5/6" />
              <SkeletonText className="w-4/5" />
            </div>
            <SkeletonBlock className="mx-auto h-10 w-32 rounded-md" />
          </div>
        </div>
        <div className="rounded-lg bg-blue-50/50 p-4">
          <SkeletonText className="mx-auto w-3/4" />
          <SkeletonText className="mx-auto w-2/3" />
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (fetchedInvestigation) {
      setInvestigation(fetchedInvestigation);
      setCreditReport(fetchedInvestigation.credit_report);
    }
  }, [fetchedInvestigation]);

  useEffect(() => {
    if (error && (error as any).response?.status === HttpStatusCode.NotFound) {
      navigate("/not-found");
    }
  }, [error, navigate]);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const displayStep = () => {
    if (!investigation || !creditReport) {
      return null;
    }

    // Show invalidated state if needed
    if (isInvalidated) {
      return <Invalidated />;
    }

    switch (currentStep) {
      case 1:
        return <Welcome setStep={(newStep) => {
          setCurrentStep(newStep + 1); // Welcome uses 0-based, we use 1-based
          setTimeout(scrollToTop, 50);
        }} />;
      case 2:
        return (
          <CapturingInputs
            ref={capturingInputsRef}
            investigation={investigation}
            creditReport={creditReport}
            setCreditReport={setCreditReport}
          />
        );
      case 3:
        return (
          <PinConfirm
            ref={pinConfirmRef}
            email={investigation.prospectus_email}
            creditReport={creditReport}
            setCreditReport={setCreditReport}
            onSubmittingChange={setIsPinSubmitting}
          />
        );
      case 4:
        return (
          <ThankYou
            metadata={creditReport.metadata ?? null}
            error_type={creditReport.error_type}
          />
        );
      default:
        return null;
    }
  };

  const setStepByStatus = () => {
    if (creditReport) {
      if (!creditReport.is_active) {
        toast.error("Reporte desactivado");
        setIsInvalidated(true);
        return;
      }

      if (creditReport.status === "invalid") {
        toast.error("Reporte invalidado");
        setIsInvalidated(true);
        return;
      }

      // Map statuses to steps (1-based)
      if (
        creditReport.status === "capturing_inputs" &&
        investigation?.status == "created"
      ) {
        setCurrentStep(1); // Welcome
      } else if (
        creditReport.status === "capturing_inputs" &&
        investigation?.status == "in_progress"
      ) {
        setCurrentStep(2); // Capturing Inputs
      } else if (creditReport.status === "pin_validation") {
        setCurrentStep(3); // PIN Confirm
      } else if (creditReport.status === "long_report") {
        toast.info("Reporte Largo");
        setCurrentStep(4); // Thank You
      } else if (creditReport.status === "completed") {
        if (creditReport.error_type) {
          toast.warning("El reporte se completó con observaciones.");
        } else {
          toast.success("Reporte completado satisfactoriamente!");
        }
        setCurrentStep(4); // Thank You
      }
    }
  };

  useEffect(() => {
    setStepByStatus();
  }, [creditReport?.status, creditReport?.error_type]);

  const getStepProgress = () => {
    // Calculate progress based on current step
    if (currentStep === 1) return 0;
    if (currentStep === 2) return 33;
    if (currentStep === 3) return 66;
    if (currentStep === 4) return 100;
    return 0;
  };

  const headerSkeleton = (
    <div className="space-y-4">
      <SkeletonHeading className="h-7 w-72" />
      <StepsSkeleton steps={4} />
    </div>
  );

  const actionsSkeleton = (
    <div className="flex items-center justify-end gap-3">
      <SkeletonBadge className="h-6 w-20" />
    </div>
  );

  return (
    <div id="express-report" className="flex min-h-screen items-center bg-gray-50/50 py-8 px-4">
      <div className="mx-auto max-w-4xl">
        <PageLoadingContainer
          showSkeleton={showSkeleton}
          slowState={slowStateBanner}
          headerSkeleton={headerSkeleton}
          actionsSkeleton={actionsSkeleton}
          contentSkeleton={contentSkeleton}
          ariaLabel="Cargando reporte exprés..."
        >
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50">
            {/* Header */}
            <div className="sticky top-0 z-10 border-b border-slate-100 bg-white/80 p-4 backdrop-blur-md sm:p-5">
              <div>
                <img
                  src={logo}
                  alt="Multiburo"
                  className="h-12 w-auto transition-all sm:h-14"
                />
              </div>
            </div>

            {/* Content */}
            <div
              ref={scrollContainerRef}
              className="relative overflow-y-auto p-4 sm:p-8"
              style={{ maxHeight: "calc(100vh - 250px)" }}
            >
              <div className="min-h-[350px]">
                {displayStep()}
              </div>
            </div>

            {/* Footer - Step indicator and action button */}
            {!isInvalidated && currentStep < 4 && (
              <div className="border-t border-slate-100 bg-white p-4 sm:px-8 sm:py-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  {/* Left side - Step indicator */}
                  <div className="flex items-center justify-center sm:justify-start">
                    <StepIndicator
                      steps={steps}
                      currentStep={currentStep}
                      size="small"
                      onStepClick={() => {}}
                      getStepProgress={getStepProgress}
                    />
                  </div>

                  {/* Right side - Action buttons */}
                  <div className="flex items-center justify-end gap-3">
                    {currentStep === 1 && (
                      <Button
                        onClick={() => {
                          setCurrentStep(2);
                          setTimeout(scrollToTop, 50);
                        }}
                        size="lg"
                        className="bg-[#0060ff] font-medium text-white transition-all hover:bg-[#0052d4] active:scale-[0.98]"
                      >
                        Comenzar
                        <svg
                          className="ml-2 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Button>
                    )}
                    {currentStep === 2 && (
                      <Button
                        onClick={() => capturingInputsRef.current?.triggerSubmit()}
                        size="lg"
                        className="bg-[#0060ff] font-medium text-white transition-all hover:bg-[#0052d4] active:scale-[0.98]"
                      >
                        Siguiente
                        <svg
                          className="ml-2 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Button>
                    )}
                    {currentStep === 3 && (
                      <Button
                        onClick={() => pinConfirmRef.current?.triggerSubmit()}
                        disabled={isPinSubmitting}
                        size="lg"
                        className="bg-[#0060ff] font-medium text-white transition-all hover:bg-[#0052d4] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-80"
                      >
                        {isPinSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Consultando tu reporte…
                          </>
                        ) : (
                          "Autorizar"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </PageLoadingContainer>
      </div>
    </div>
  );
};

export default ExpressReport;
